import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import React, { useState } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import { FiPlus, FiTrash2, FiBarChart2 } from 'react-icons/fi';

// Stat Item Component
const StatItemComponent = ({ item, index, onUpdate, onDelete, isEditing }) => {
  const handleValueChange = (e) => {
    onUpdate(index, { ...item, value: e.target.value });
  };

  const handleLabelChange = (e) => {
    onUpdate(index, { ...item, label: e.target.value });
  };

  return (
    <div className="stat-item">
      {isEditing ? (
        <>
          <input
            type="text"
            value={item.value}
            onChange={handleValueChange}
            placeholder="100+"
            className="stat-value-input"
          />
          <input
            type="text"
            value={item.label}
            onChange={handleLabelChange}
            placeholder="Label"
            className="stat-label-input"
          />
          <button
            type="button"
            onClick={() => onDelete(index)}
            className="stat-delete-btn"
          >
            <FiTrash2 size={12} />
          </button>
        </>
      ) : (
        <>
          <div className="stat-value">{item.value || '0'}</div>
          <div className="stat-label">{item.label || 'Label'}</div>
        </>
      )}
    </div>
  );
};

// Stats Node View Component
const StatsNodeView = ({ node, updateAttributes, selected }) => {
  const [isEditing, setIsEditing] = useState(true);
  const items = node.attrs.items || [];

  const handleUpdateItem = (index, newItem) => {
    const newItems = [...items];
    newItems[index] = newItem;
    updateAttributes({ items: newItems });
  };

  const handleDeleteItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    updateAttributes({ items: newItems });
  };

  const handleAddItem = () => {
    updateAttributes({
      items: [...items, { value: '', label: '' }],
    });
  };

  return (
    <NodeViewWrapper className={`stats-block-wrapper ${selected ? 'selected' : ''}`}>
      <div className="stats-header">
        <FiBarChart2 size={16} />
        <span>Statistics</span>
        <button
          type="button"
          onClick={() => setIsEditing(!isEditing)}
          className="stats-edit-toggle"
        >
          {isEditing ? 'Preview' : 'Edit'}
        </button>
      </div>
      <div className="stats-block">
        {items.map((item, index) => (
          <StatItemComponent
            key={index}
            item={item}
            index={index}
            onUpdate={handleUpdateItem}
            onDelete={handleDeleteItem}
            isEditing={isEditing}
          />
        ))}
      </div>
      {isEditing && (
        <button type="button" onClick={handleAddItem} className="stats-add-btn">
          <FiPlus size={16} />
          <span>Add Stat</span>
        </button>
      )}
    </NodeViewWrapper>
  );
};

// Stats Extension
export const Stats = Node.create({
  name: 'stats',
  group: 'block',
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      items: {
        default: [
          { value: '10K+', label: 'Happy Customers' },
          { value: '99%', label: 'Satisfaction Rate' },
          { value: '24/7', label: 'Support' },
          { value: '5 Stars', label: 'Average Rating' },
        ],
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="stats"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'stats' }), 0];
  },

  addNodeView() {
    return ReactNodeViewRenderer(StatsNodeView);
  },

  addCommands() {
    return {
      insertStats:
        (attrs = {}) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              items: attrs.items || [
                { value: '10K+', label: 'Happy Customers' },
                { value: '99%', label: 'Satisfaction Rate' },
                { value: '24/7', label: 'Support' },
              ],
            },
          });
        },
    };
  },
});

export default Stats;
